import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [emailHint, setEmailHint] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) { setError("Missing reset token"); setLoading(false); return; }
    api.get(`/auth/reset-password/${token}`)
      .then((r) => { setValid(true); setEmailHint(r.data.email || ""); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid or expired reset link"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pwd !== pwd2) { setError("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F5F4F0]">
      <div className="w-full max-w-md card-soft p-8 md:p-10 fade-in-up" data-testid="reset-password-page">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#5C7A4F] text-white mb-4">
          <KeyRound size={22} />
        </div>
        <h2 className="font-heading text-3xl font-semibold text-[#2C342A]">Set a new password</h2>
        {emailHint && !done && <p className="text-[#707A6E] mt-1 mb-6">for <b>{emailHint}</b></p>}

        {loading ? (
          <p className="text-[#707A6E] mt-4">Checking your link…</p>
        ) : done ? (
          <div className="mt-4 space-y-4" data-testid="reset-done-state">
            <div className="rounded-xl bg-emerald-500/10 text-emerald-800 px-4 py-3 text-sm">
              Password reset. Redirecting to sign in…
            </div>
          </div>
        ) : valid ? (
          <form onSubmit={submit} className="space-y-4 mt-6" data-testid="reset-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">New password</label>
              <input required type="password" minLength={6} data-testid="reset-pwd-input" value={pwd}
                     onChange={(e) => setPwd(e.target.value)}
                     className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-[#707A6E]">Confirm password</label>
              <input required type="password" minLength={6} data-testid="reset-pwd2-input" value={pwd2}
                     onChange={(e) => setPwd2(e.target.value)}
                     className="mt-1 w-full rounded-xl border border-[#E4E2DC] bg-white px-4 py-3 outline-none focus:border-[#5C7A4F]" />
            </div>
            {error && <div className="text-sm text-[#C06E52] bg-[#C06E52]/10 rounded-xl px-3 py-2" data-testid="reset-error">{error}</div>}
            <button data-testid="reset-submit-btn" disabled={submitting}
                    className="w-full rounded-xl bg-[#5C7A4F] text-white px-6 py-3 font-medium hover:bg-[#4B6640] disabled:opacity-60">
              {submitting ? "Updating…" : "Update password"}
            </button>
          </form>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="rounded-xl bg-[#C06E52]/10 text-[#C06E52] px-4 py-3 text-sm" data-testid="reset-invalid">
              {error || "Invalid or expired reset link"}
            </div>
            <Link to="/forgot-password" className="text-[#5C7A4F] hover:underline" data-testid="reset-try-again">
              Request a new link
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
